// ═══════════════════════════════════════════════════════════════
// PACE — My PACE Profile
// Edit personal PACE tiers (P-A-C-E contact info)
// ═══════════════════════════════════════════════════════════════

import { useEffect } from 'react';
import {
  Box, Title, Text, Paper, Group, Stack, Center, Loader,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import { useAuthStore } from '../../store/authStore';
import { usePaceStore, PACE_TIERS } from '../../store/paceStore';
import type { PaceTier, CommMethod, PaceProfile } from '../../store/paceStore';
import PaceTierCard from './components/PaceTierCard';

export default function MyPace() {
  const { user } = useAuthStore();
  const { profiles, loading, loadProfiles, saveProfile } = usePaceStore();
  useEffect(() => { loadProfiles(); }, []);

  const myProfiles = profiles.filter(p => p.personEmail === user?.email);
  const getEntry = (tier: PaceTier): PaceProfile | undefined =>
    myProfiles.find(p => p.tier === tier && p.contactValue);

  const completedTiers = PACE_TIERS.filter(t => !!getEntry(t)).length;
  const isComplete = completedTiers === 4;

  const handleSave = async (tier: PaceTier, data: {
    method: CommMethod; contactValue: string; backupValue: string; notes: string;
  }) => {
    if (!user) return;
    try {
      await saveProfile({
        personName: user.fullName ?? user.email ?? '',
        personEmail: user.email ?? '',
        personId: user.email ?? '',
        tier,
        method: data.method,
        contactValue: data.contactValue,
        backupValue: data.backupValue,
        notes: data.notes,
        department: '',
        functionTitle: '',
      });
      notifications.show({ message: `${tier.charAt(0).toUpperCase() + tier.slice(1)} tier saved!`, color: 'green' });
    } catch (err: any) {
      notifications.show({ message: err?.message || 'Failed to save', color: 'red' });
    }
  };

  if (loading && profiles.length === 0) {
    return <Center h={300}><Loader size="md" /></Center>;
  }

  return (
    <Box>
      <Box mb="lg">
        <Title order={3} c="black">My PACE Profile</Title>
        <Text c="dimmed" mt={4}>
          Enter your contact information for each communication tier. The system requires at least one method per tier.
        </Text>
      </Box>

      {/* Completion status */}
      <Paper p="md" mb="lg" withBorder style={{
        borderColor: isComplete ? '#bbf7d0' : '#fde68a',
        background: isComplete ? '#f0fdf4' : '#fffbeb',
      }}>
        <Group align="flex-start" gap="sm" wrap="nowrap">
          <Box style={{ flex: 1 }}>
            <Text fw={500} size="sm" style={{ color: isComplete ? '#166534' : '#92400e' }}>
              {isComplete ? 'Profile Complete' : `${completedTiers} of 4 tiers completed`}
            </Text>
            <Text size="xs" mt={2} style={{ color: isComplete ? '#15803d' : '#a16207' }}>
              {isComplete
                ? 'All PACE tiers are filled in. Remember to re-verify every 180 days.'
                : 'Complete all four tiers to be marked compliant.'
              }
            </Text>
          </Box>
          <Group gap={4}>
            {PACE_TIERS.map(t => (
              <div key={t} style={{
                width: 8, height: 8, borderRadius: '50%',
                background: getEntry(t) ? '#22c55e' : '#d1d5db',
              }} title={t} />
            ))}
          </Group>
        </Group>
      </Paper>

      {/* PACE Tier Cards */}
      <Stack gap="md">
        {PACE_TIERS.map(tier => (
          <PaceTierCard
            key={tier}
            tier={tier}
            entry={getEntry(tier)}
            onSave={handleSave}
          />
        ))}
      </Stack>

      {/* Guidance */}
      <Paper p="md" mt="lg" withBorder style={{ background: '#f8fafc' }}>
        <Text fw={600} size="sm" c="dimmed" mb="xs">PACE Principles</Text>
        <Stack gap={4}>
          <Text size="xs" c="dimmed"><Text span fw={700} c="green">Primary</Text> — Your first choice. Used under normal conditions.</Text>
          <Text size="xs" c="dimmed"><Text span fw={700} c="blue">Alternate</Text> — Backup if primary fails. Should be a different method.</Text>
          <Text size="xs" c="dimmed"><Text span fw={700} c="yellow.7">Contingent</Text> — Degraded conditions. Infrastructure may be impaired.</Text>
          <Text size="xs" c="dimmed"><Text span fw={700} c="red">Emergency</Text> — Last resort. Assume minimal infrastructure.</Text>
        </Stack>
      </Paper>
    </Box>
  );
}
